/**
 * Pure timer helpers for a countdown: the exam session view (brief §exam: "countdown from
 * server deadline with warnings at 30 and 5 min") and the practice timed pair (pedagogy spec
 * §7.3.6). Shared from `client/src/lib` so practice imports no other feature; the exam feature
 * re-exports it from `features/exam/countdown.ts`.
 */
/**
 * Review-A Important 5: a visible `warn1` level, matching the spoken warning
 * at 1 minute left (`crossedAnnounceThreshold` below) — the badges are the
 * text-not-colour-alone signal the brief asked for (WCAG 1.4.1), so they must
 * exist at every threshold the countdown speaks, not stop one level short.
 */
export type CountdownLevel = "normal" | "warn30" | "warn5" | "warn1" | "over";

const THIRTY_MIN_MS = 30 * 60_000;
const FIVE_MIN_MS = 5 * 60_000;
const ONE_MIN_MS = 60_000;

export function countdownLevel(remainingMs: number): CountdownLevel {
  if (remainingMs <= 0) return "over";
  if (remainingMs <= ONE_MIN_MS) return "warn1";
  if (remainingMs <= FIVE_MIN_MS) return "warn5";
  if (remainingMs <= THIRTY_MIN_MS) return "warn30";
  return "normal";
}

/**
 * The moments a screen-reader user should be told the time left out loud
 * (UX-01): 30, 5 and 1 minutes remaining — not the ticking digits, which
 * would otherwise re-announce every second and drown the problem text.
 * Ascending order matters: `crossedAnnounceThreshold` below reports the
 * *lowest* (most urgent) of these crossed in one jump, so this is the order
 * it iterates in.
 */
export const COUNTDOWN_ANNOUNCE_THRESHOLDS_MS: readonly number[] = [
  ONE_MIN_MS,
  FIVE_MIN_MS,
  THIRTY_MIN_MS,
];

/**
 * The threshold (if any) the countdown just crossed going from `prevMs` to
 * `nextMs`, for a one-time spoken warning rather than a per-tick one. `null`
 * when no threshold was crossed between the two readings.
 *
 * Batch O's resync (a wake/online refetch, or a bounded clock-jump hold)
 * makes a jump across several thresholds in one tick routine — a sleeping
 * laptop can wake straight from 40 minutes left to 30 seconds left. Reporting
 * the *highest* threshold crossed (the review's original behaviour) would
 * then speak "30 minuter kvar" with 30 seconds actually left, the least
 * urgent and most misleading reading of the three. Iterating the ascending
 * list and returning on the first match reports the lowest — most urgent —
 * threshold crossed instead. A jump straight past zero (`nextMs <= 0`) is
 * not a "1 minut kvar" warning at all — that is the same tick as auto-submit
 * — so it announces nothing here.
 */
export function crossedAnnounceThreshold(prevMs: number, nextMs: number): number | null {
  if (nextMs <= 0) return null;
  for (const threshold of COUNTDOWN_ANNOUNCE_THRESHOLDS_MS) {
    if (prevMs > threshold && nextMs <= threshold) return threshold;
  }
  return null;
}

/**
 * Which of `thresholds` (ms left; 0 means "reached zero") the countdown crossed going from `prevMs`
 * to `nextMs`, or null. A jump across several reports the lowest, most urgent one, like
 * `crossedAnnounceThreshold`, but a jump to or past zero reports 0: the timed pair keeps running
 * at zero (nothing is auto-submitted), so its zero is a message, not a submit.
 */
export function crossedThreshold(
  prevMs: number,
  nextMs: number,
  thresholds: readonly number[],
): number | null {
  for (const threshold of [...thresholds].sort((a, b) => a - b)) {
    if (prevMs > threshold && nextMs <= threshold) return threshold;
  }
  return null;
}
